import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import Disconnect from '@app/views/Disconnect';
import Invoice from '@app/views/Invoice';
import { NetworkManagement } from '@app/views/Network/network-management';
import { BridgeMode } from '@app/views/Network/network-mode/BridgeMode';
import { WifiMode } from '@app/views/Network/network-mode/WifiMode';
import Overview from '@app/views/Overview';
import ScheduleInstall from '@app/views/ScheduleInstall';
import { SignContract } from '@app/views/SignContract';
import { Stream } from '@app/views/Stream';
import HeaderNavigation from '@app/components/HeaderNavigation';
import MobileTabBar from '@app/components/MobileTabBar';
import { PageFooter } from '@app/components/PageFooter';
import { selectHasFiber, selectIsFiberServiceable, selectUser } from '@app/redux';
import { selectConfig } from '@app/redux/config';

import { Platform, ProductInstanceStatus } from '@mfe/shared/schema-types';

import { AccountSettingsSwitch, BillingSwitch, NetworkSwitch, PlanSwitch, ShopAddonsSwitch } from './NestedSwitch';
import { ROUTES } from './constants';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { platform } = useSelector(selectConfig);
  const isWeb = platform === Platform.Web;

  return (
    <div
      style={{
        minHeight: '100vh',
        flexGrow: 1,
        backgroundColor: '#F2F5F8',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {isWeb && <HeaderNavigation />}
      {children}
      {isWeb ? <PageFooter /> : <MobileTabBar />}
    </div>
  );
};

const AppRouter: React.FC = () => {
  const isFiber = useSelector(selectHasFiber);
  const isServiceable = useSelector(selectIsFiberServiceable);
  const allowScheduleInstallationRoute = isFiber ? isServiceable : true;

  const { user } = useSelector(selectUser);
  const allowDisconnectRoute = user?.productInstanceStatus !== ProductInstanceStatus.Canceled;

  return (
    <Layout>
      <Switch>
        {/* Main routes */}
        <Route exact path={ROUTES.HOME} component={Overview} />

        <Route path={ROUTES.SIGN_CONTRACT} component={SignContract} />
        <Route path={ROUTES.NETWORK_MANAGEMENT} component={NetworkManagement} />
        <Route path={ROUTES.NETWORK_WIFI} component={WifiMode} />
        <Route path={ROUTES.NETWORK_BRIDGE} component={BridgeMode} />
        <Route path={ROUTES.BILLING} component={BillingSwitch} />
        <Route path={ROUTES.NETWORK} component={NetworkSwitch} />
        <Route path={ROUTES.PLAN} component={PlanSwitch} />
        <Route path={ROUTES.PROFILE} component={AccountSettingsSwitch} />
        <Route path={ROUTES.STREAM} component={Stream} />
        <Route path={`${ROUTES.INVOICE}/:invoice`} component={Invoice} />
        <Route path={ROUTES.SHOP_ADDONS} component={ShopAddonsSwitch} />

        {allowScheduleInstallationRoute && <Route path={ROUTES.SCHEDULE_INSTALLATION} component={ScheduleInstall} />}

        {allowDisconnectRoute && <Route path={ROUTES.DISCONNECT} component={Disconnect} />}

        {/* Default route */}
        <Route path={ROUTES.HOME} render={() => <Redirect to={ROUTES.HOME} />} />

        {/* Catch all route */}
        <Route path="*" render={() => <Redirect to={ROUTES.HOME} />} />
      </Switch>
    </Layout>
  );
};

export { AppRouter };
